import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { 
  FileText, 
  Image, 
  Upload, 
  Download, 
  Trash2, 
  Eye,
  Map,
  FileSpreadsheet,
  Camera,
  Loader2,
  ClipboardList

} from 'lucide-react';

interface OrdenTrabajoDocumento {
  id: string;
  orden_trabajo_id: string;
  nombre_archivo: string;
  tipo_documento: string;
  url_archivo: string;
  tamaño_archivo: number | null;
  descripcion: string | null;
  created_at: string;
}

interface OrdenTrabajoDocumentosProps {
  ordenTrabajoId: string;
  readonly?: boolean;
}

const TIPOS_DOCUMENTO = [
  { value: 'albaran_trabajos', label: 'Albarán de trabajos', icon: ClipboardList },
  { value: 'plano', label: 'Plano', icon: Map },
  { value: 'especificacion', label: 'Especificación técnica', icon: FileSpreadsheet },
  { value: 'foto_sitio', label: 'Foto del sitio', icon: Camera },
  { value: 'documentacion_cliente', label: 'Documentación cliente', icon: FileText },
  { value: 'presupuesto', label: 'Presupuesto', icon: FileSpreadsheet },
  { value: 'otro', label: 'Otro', icon: FileText },
];


export const OrdenTrabajoDocumentos: React.FC<OrdenTrabajoDocumentosProps> = ({ 
  ordenTrabajoId, 
  readonly = false 
}) => {
  const [documentos, setDocumentos] = useState<OrdenTrabajoDocumento[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState('otro');
  const [descripcion, setDescripcion] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchDocumentos = async () => {
    try {
      const { data, error } = await supabase
        .from('orden_trabajo_documentos')
        .select('*')
        .eq('orden_trabajo_id', ordenTrabajoId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocumentos(data || []);
    } catch (error) {
      console.error('Error fetching documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ordenTrabajoId) {
      fetchDocumentos();
    }
  }, [ordenTrabajoId]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const { compressImageForUpload } = await import('@/lib/imageUtils');
      setOptimizing(true);
      const uploadFile = await compressImageForUpload(file);
      setOptimizing(false);
      const fileExt = uploadFile.name.split('.').pop();
      const fileName = `${ordenTrabajoId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('orden-trabajo-documentos')
        .upload(fileName, uploadFile, { contentType: uploadFile.type });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('orden-trabajo-documentos')
        .getPublicUrl(fileName);

      const { error: insertError } = await supabase
        .from('orden_trabajo_documentos')
        .insert({
          orden_trabajo_id: ordenTrabajoId,
          nombre_archivo: file.name,
          tipo_documento: tipoDocumento,
          url_archivo: publicUrl,
          tamaño_archivo: uploadFile.size,
          descripcion: descripcion || null,
        });

      if (insertError) throw insertError;

      toast({
        title: "Documento subido",
        description: "El documento se ha subido correctamente",
      });

      setDescripcion('');
      setTipoDocumento('otro');
      fetchDocumentos();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error",
        description: "Error al subir el documento",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async (documento: OrdenTrabajoDocumento) => {
    try {
      const link = document.createElement('a');
      link.href = documento.url_archivo;
      link.download = documento.nombre_archivo;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: "Error",
        description: "Error al descargar el documento",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (documento: OrdenTrabajoDocumento) => {
    try {
      const urlParts = documento.url_archivo.split('/');
      const filePath = urlParts.slice(-2).join('/');

      await supabase.storage
        .from('orden-trabajo-documentos')
        .remove([filePath]);

      const { error } = await supabase
        .from('orden_trabajo_documentos')
        .delete()
        .eq('id', documento.id);

      if (error) throw error;

      toast({
        title: "Documento eliminado",
        description: "El documento se ha eliminado correctamente",
      });

      fetchDocumentos();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        title: "Error",
        description: "Error al eliminar el documento",
        variant: "destructive",
      });
    }
  };

  const getDocumentIcon = (tipo: string) => {
    const tipoDoc = TIPOS_DOCUMENTO.find(t => t.value === tipo);
    const IconComponent = tipoDoc?.icon || FileText;
    return <IconComponent className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return 'Desconocido';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageFile = (fileName: string) => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ext && imageExtensions.includes(ext);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!readonly && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de documento</Label>
                <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOCUMENTO.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        <div className="flex items-center gap-2">
                          <tipo.icon className="h-4 w-4" />
                          {tipo.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Input
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Descripción del documento"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                disabled={uploading || optimizing}
                onClick={() => document.getElementById('orden-doc-upload')?.click()}
              >
                {(uploading || optimizing) ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {optimizing ? 'Optimizando…' : uploading ? 'Subiendo…' : 'Subir documento'}
              </Button>
              <input
                id="orden-doc-upload"
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf"
              />
              <span className="text-sm text-muted-foreground">
                Formatos: Imágenes, PDF, Word, Excel, CAD
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {documentos.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No hay documentos adjuntos</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {documentos.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isImageFile(doc.nombre_archivo) ? (
                    <Image className="h-5 w-5 text-primary" />
                  ) : (
                    getDocumentIcon(doc.tipo_documento)
                  )}
                  <div>
                    <p className="font-medium text-sm">{doc.nombre_archivo}</p>
                    <p className="text-xs text-muted-foreground">
                      {TIPOS_DOCUMENTO.find(t => t.value === doc.tipo_documento)?.label || 'Otro'} • {formatFileSize(doc.tamaño_archivo)} • {new Date(doc.created_at).toLocaleDateString('es-ES')}
                    </p>
                    {doc.descripcion && (
                      <p className="text-xs text-muted-foreground mt-1">{doc.descripcion}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isImageFile(doc.nombre_archivo) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setPreviewImage(doc.url_archivo);
                        setPreviewOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDownload(doc)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {!readonly && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer. Se eliminará permanentemente el documento "{doc.nombre_archivo}".
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(doc)}>
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Vista previa</DialogTitle>
          </DialogHeader>
          {previewImage && (
            <img
              src={previewImage}
              alt="Vista previa"
              className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
